import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';

import { GET_INSTALL_HEALTH } from 'src/api/queries/getInstallHealth';
import type { GetInstallHealthResponse, InstallHealth } from 'src/api/queries/getInstallHealth';
import type {
  ForecastReadinessStatus,
  HistoricalOrdersProgress,
} from 'src/api/queries/getLastUpdatedAt';
import { isActiveInstallPhase, isTerminalInstallPhase, withinHours } from 'src/utils/install-phase';

/** Poll cadence (spec §2): active installs poll briskly, slowing when the
 *  tab is backgrounded; terminal/complete states and error backoff poll slow. */
const POLL_ACTIVE_FOCUSED = 30_000;
const POLL_ACTIVE_HIDDEN = 60_000;
/** The insight chain ("Building your inventory insights") and the post-import
 *  wrap-up resolve in ~seconds. Poll briskly through them so the card animates
 *  instead of freezing between slow polls. */
const POLL_CHAIN_FOCUSED = 7_000;
const POLL_CHAIN_HIDDEN = 20_000;
const POLL_SLOW = 300_000;

interface UseInstallHealthReturn {
  installHealth: InstallHealth | null;
  historicalOrdersProgress: HistoricalOrdersProgress | null;
  /** READY when the early-chain forecast pass has computed forecasts for
   *  this company. Drives the setup page's "See sales forecasts"
   *  capability flag — `install_phase === 'FULL_READY'` is too coarse
   *  because forecasts become usable at RECENT_READY's chain pass. */
  forecastReadinessStatus: ForecastReadinessStatus | null;
  loading: boolean;
  error: boolean;
  /** Force an immediate refetch — wired to the manual "Check status" button. */
  refetch: () => void;
}

/**
 * Reads `installHealth` for the first-install banner and setup page.
 *
 * Polls only while an install is actually in flight. A null `installHealth`
 * (existing/pre-tracking company — the common case) means no banner and no
 * polling; a `FULL_READY` install is polled only inside its 24h banner window.
 */
export function useInstallHealth(): UseInstallHealthReturn {
  const { data, loading, error, refetch, startPolling, stopPolling } =
    useQuery<GetInstallHealthResponse>(GET_INSTALL_HEALTH, {
      notifyOnNetworkStatusChange: true,
    });

  const installHealth = data?.installHealth ?? null;
  const historicalOrdersProgress = data?.getLastUpdatedAt?.historicalOrdersProgress ?? null;
  const forecastReadinessStatus = data?.getLastUpdatedAt?.forecastReadinessStatus ?? null;
  const phase = installHealth?.installPhase ?? null;
  // The insight chain runs at install_phase === 'FULL_READY' with
  // install_chain_status running/queued, and the post-import wrap-up sits at
  // the FINALIZING sub-state. Both are user-visible work that resolves in
  // seconds but is NOT an "active install phase" — so the brisk-poll window
  // must key off these signals, not just the phase, or the liveliest part of
  // setup polls on the 5-minute slow cadence.
  const chainActive =
    installHealth?.installChainStatus === 'queued' ||
    installHealth?.installChainStatus === 'running';
  const finalizing = installHealth?.loadingOrdersSubState === 'FINALIZING';
  // Onboarded (FULL_READY) but the "See sales forecasts" capability is still
  // pending. This is the settled-yet-incomplete window: the chain ended
  // without writing forecasts (produced none, or failed) and a later run will
  // fill them in. `install_phase` has stopped advancing and the chain is no
  // longer active, so without this signal the cadence falls through to the
  // 5-minute slow poll and the row appears frozen until a manual "Check
  // status". Scoped to FULL_READY so terminal phases (DEGRADED/BROWSE_ONLY),
  // where forecasts are not expected to arrive, are not brisk-polled.
  const forecastResolving =
    phase === 'FULL_READY' &&
    forecastReadinessStatus != null &&
    forecastReadinessStatus !== 'READY';

  // Backgrounded tabs poll at half cadence.
  const [isVisible, setIsVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState === 'visible'
  );
  useEffect(() => {
    const onVisibility = () => setIsVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const interval = useMemo(() => {
    // Back off the cadence while the endpoint is failing.
    if (error) return POLL_SLOW;
    // Brisk window: the insight chain and post-import wrap-up resolve in
    // seconds. The chain runs at FULL_READY (not an active phase), so this
    // clause must precede the active-phase check.
    if (chainActive || finalizing) {
      return isVisible ? POLL_CHAIN_FOCUSED : POLL_CHAIN_HIDDEN;
    }
    if (phase == null || isActiveInstallPhase(phase)) {
      return isVisible ? POLL_ACTIVE_FOCUSED : POLL_ACTIVE_HIDDEN;
    }
    // Settled at FULL_READY with forecasts still resolving: hold the active
    // cadence so the capability row flips on its own when forecasts land,
    // rather than stranding the merchant on the slow poll right after
    // "Successfully onboarded".
    if (forecastResolving) {
      return isVisible ? POLL_ACTIVE_FOCUSED : POLL_ACTIVE_HIDDEN;
    }
    return POLL_SLOW;
  }, [error, chainActive, finalizing, forecastResolving, phase, isVisible]);

  const shouldPoll = useMemo(() => {
    // The one-shot initial fetch handles the unknown state; nothing to poll yet.
    if (!data) return false;
    // No CompanySyncState row → existing company → no banner, no polling.
    if (phase == null) return false;
    if (isActiveInstallPhase(phase) || isTerminalInstallPhase(phase)) return true;
    // FULL_READY while the insight chain is still running or wrapping up.
    if (chainActive || finalizing) return true;
    // FULL_READY: keep polling only inside the 24h "Setup complete" window.
    if (phase === 'FULL_READY') return withinHours(installHealth?.phaseEnteredAt, 24);
    return false;
  }, [data, phase, chainActive, finalizing, installHealth?.phaseEnteredAt]);

  useEffect(() => {
    if (shouldPoll) {
      startPolling(interval);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [shouldPoll, interval, startPolling, stopPolling]);

  const handleRefetch = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    installHealth,
    historicalOrdersProgress,
    forecastReadinessStatus,
    loading,
    error: Boolean(error),
    refetch: handleRefetch,
  };
}

/** Window the backend enforces between `recent_ready_at` and the demand gate
 *  opening (24h). Mirrors the backend logic in
 *  `inventory-backend/src/services/install_phase/recommendation_gate.py`. */
const DEMAND_GATE_OBSERVATION_HOURS = 24;

/**
 * Reads only the demand-gate verdict, without polling — for recommendation
 * and inventory tables that gate demand-derived columns (spec §3). The
 * install banner's own poll keeps the cache fresh; this is a cache-first
 * read. Defaults to `true` (ungated) when install health is unknown, which
 * is correct for existing/pre-tracking companies.
 *
 * `hoursUntilOpen` is null when the gate is already open OR when the gate's
 * 24h observation window has not started yet (no `recentReadyAt`). A finite
 * value is the rounded number of hours the merchant must wait before
 * demand-derived columns light up — useful for "Recommendations ready in ~Xh"
 * hints. The value floors at 1 (we never say "ready in 0 hours" while still
 * gated; that's confusing) and is recomputed from the cache on every read.
 */
export function useDemandGate(): {
  canShowDemandRecommendations: boolean;
  hoursUntilOpen: number | null;
  loading: boolean;
} {
  const { data, loading } = useQuery<GetInstallHealthResponse>(GET_INSTALL_HEALTH, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });
  const canShowDemandRecommendations = data?.installHealth?.canShowDemandRecommendations ?? true;
  const recentReadyAt = data?.installHealth?.recentReadyAt ?? null;

  const hoursUntilOpen = (() => {
    if (canShowDemandRecommendations) return null;
    if (!recentReadyAt) return null;
    const elapsed = Date.now() - new Date(recentReadyAt).getTime();
    if (!Number.isFinite(elapsed)) return null;
    const remainingMs = DEMAND_GATE_OBSERVATION_HOURS * 3_600_000 - elapsed;
    if (remainingMs <= 0) return null;
    return Math.max(1, Math.ceil(remainingMs / 3_600_000));
  })();

  return {
    canShowDemandRecommendations,
    hoursUntilOpen,
    loading,
  };
}
